import { useState } from "react";
import { Save, X, Edit2, Trash2, KeyRound } from "lucide-react";
import DropdownMenu from "../common/DropdownMenu";
import { EditButton, DeleteButton } from "../common/ActionButtons";
import { ROLES } from "../../config/constants";

/**
 * EditableUserRow Component
 * Row yang bisa diedit inline dengan mode view/edit untuk User Management
 */
export function EditableUserRow({
  user,
  index,
  projects,
  onSave,
  onDelete,
  onResetPassword,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    nama: user.nama,
    project_id: user.project_id,
    hp: user.hp,
    role: user.role || ROLES.USER,
  });

  const handleSave = async () => {
    await onSave(user.id, editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      nama: user.nama,
      project_id: user.project_id,
      hp: user.hp,
      role: user.role || ROLES.USER,
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    console.log("[EditableUserRow] handleEdit - Current user:", user);
    setEditData({
      nama: user.nama,
      project_id: user.project_id || "",
      hp: user.hp,
      role: user.role || ROLES.USER,
    });
    setIsEditing(true);
  };

  return (
    <tr className="border-t hover:bg-gray-50 transition">
      <td className="p-3">{index + 1}</td>

      {/* Nama - Editable */}
      <td className="p-3">
        {isEditing ? (
          <input
            type="text"
            value={editData.nama}
            onChange={(e) => setEditData({ ...editData, nama: e.target.value })}
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        ) : (
          <span className="font-medium">{user.nama}</span>
        )}
      </td>

      {/* Email - Read Only */}
      <td className="p-3">
        <span className="text-gray-700">{user.email || "-"}</span>
      </td>

      {/* Project - Dropdown saat edit */}
      <td className="p-3">
        {isEditing ? (
          <select
            value={editData.project_id}
            onChange={(e) =>
              setEditData({ ...editData, project_id: e.target.value })
            }
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="">-- Pilih Project --</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.division ? `${p.name} (${p.division})` : p.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="font-medium">{user.project || "-"}</span>
        )}
      </td>

      {/* HP - Editable */}
      <td className="p-3">
        {isEditing ? (
          <input
            type="tel"
            value={editData.hp}
            onChange={(e) => setEditData({ ...editData, hp: e.target.value })}
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        ) : (
          <span>{user.hp || "-"}</span>
        )}
      </td>

      {/* Role - Dropdown saat edit */}
      <td className="p-3 text-center">
        {isEditing ? (
          <select
            value={editData.role}
            onChange={(e) => {
              console.log("[EditableUserRow] Role changed to:", e.target.value);
              setEditData({ ...editData, role: e.target.value });
            }}
            className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value={ROLES.USER}>User</option>
            <option value={ROLES.PROJECT_ADMIN}>Project Admin</option>
            <option value={ROLES.SUPER_ADMIN}>Super Admin</option>
          </select>
        ) : (
          <span
            className={`px-3 py-1 text-xs rounded-full font-semibold ${
              user.role === ROLES.SUPER_ADMIN
                ? "bg-purple-100 text-purple-700"
                : user.role === ROLES.PROJECT_ADMIN
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
            }`}
          >
            {user.role === ROLES.SUPER_ADMIN
              ? "Super Admin"
              : user.role === ROLES.PROJECT_ADMIN
                ? "Project Admin"
                : "User"}
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="p-3">
        <div className="flex items-center justify-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors"
                title="Simpan"
              >
                <Save size={18} />
              </button>
              <button
                onClick={handleCancel}
                className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                title="Batal"
              >
                <X size={18} />
              </button>
            </>
          ) : (
            <DropdownMenu
              actions={[
                {
                  label: "Edit",
                  icon: <Edit2 size={16} />,
                  onClick: handleEdit,
                  variant: "default",
                },
                {
                  label: "Reset Password",
                  icon: <KeyRound size={16} />,
                  onClick: () => onResetPassword(user.id),
                  variant: "warning",
                },
                {
                  label: "Delete",
                  icon: <Trash2 size={16} />,
                  onClick: () => onDelete(user.id),
                  variant: "danger",
                },
              ]}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * EditableProjectRow Component
 * Row yang bisa diedit inline dengan mode view/edit untuk Project Management
 */
export function EditableProjectRow({
  project,
  index,
  divisions = [],
  onSave,
  onDelete,
  showActions = true,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    project: project.project,
    division_id: project.division_id || "",
  });

  const handleSave = async () => {
    await onSave(project.id, editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      project: project.project,
      division_id: project.division_id || "",
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setEditData({
      project: project.project,
      division_id: project.division_id || "",
    });
    setIsEditing(true);
  };

  return (
    <tr className="border-t hover:bg-gray-50 transition">
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {index + 1}
      </td>

      {/* Project Name - Editable */}
      <td className="px-6 py-4">
        {isEditing ? (
          <input
            type="text"
            value={editData.project}
            onChange={(e) =>
              setEditData({ ...editData, project: e.target.value })
            }
            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            autoFocus
          />
        ) : (
          <span className="text-sm text-gray-900 font-medium">
            {project.project}
          </span>
        )}
      </td>

      {/* Division - Editable */}
      <td className="px-6 py-4">
        {isEditing ? (
          <select
            value={editData.division_id}
            onChange={(e) =>
              setEditData({ ...editData, division_id: e.target.value })
            }
            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="">-- Pilih --</option>
            {divisions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-gray-900">
            {project.division_name || "-"}
          </span>
        )}
      </td>

      {/* Actions */}
      {showActions && (
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                  title="Simpan"
                >
                  <Save size={18} />
                </button>
                <button
                  onClick={handleCancel}
                  className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Batal"
                >
                  <X size={18} />
                </button>
              </>
            ) : (
              <>
                <EditButton onClick={handleEdit} />
                <DeleteButton onClick={() => onDelete(project.id)} />
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
